import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Col, Container, Row } from "reactstrap";
import { connect } from "react-redux";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { useDispatch, flashError, flashSuccess } from "@/shared/components/flash";
import { ParticipantTabNavs } from "@/shared/components/tabNavs";
import Sidebar from "@/containers/Layout/sidebar";
import ClearPageLayout from "@/containers/Participant/components/ClearPageLayout";
import { FirstStep, FourthStep, SecondStep, ThirdStep } from "./components/steps";

const CLASSIC_IMG_BACK = `${process.env.PUBLIC_URL}/theme/classic/img/pages/giveReward.png`;

const STEP_FIELDS = {
  0: ["feeling"],
  1: ["first_name", "last_name"],
  2: ["email"],
  3: [],
};

const validateStep = (step, values, t) => {
  const nextErrors = {};

  if (step === 0 && !values.feeling) {
    nextErrors.feeling = t("select_a_feeling");
  }

  if (step === 1) {
    if (!values.first_name.trim()) {
      nextErrors.first_name = t("please_enter_first_name");
    }

    if (!values.last_name.trim()) {
      nextErrors.last_name = t("please_enter_last_name");
    }
  }

  if (step === 2) {
    const email = values.email.trim();

    if (!email) {
      nextErrors.email = t("email_is_required");
    } else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(email)) {
      nextErrors.email = t("invalid_email_address");
    }
  }

  return nextErrors;
};

const validateAllSteps = (values, t) => ({
  ...validateStep(0, values, t),
  ...validateStep(1, values, t),
  ...validateStep(2, values, t),
});

const getFirstInvalidStep = (errors) => {
  if (errors.feeling) return 0;
  if (errors.first_name || errors.last_name) return 1;
  if (errors.email) return 2;
  return 3;
};

const Feeling = ({ auth, program, organization, template }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [currentStep, setCurrentStep] = useState(0);
  const [data, setData] = useState({
    feeling: "",
    first_name: "",
    last_name: "",
    email: "",
    comment: "",
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    setData((prev) => ({
      ...prev,
      first_name: prev.first_name || auth?.first_name || "",
      last_name: prev.last_name || auth?.last_name || "",
      email: prev.email || auth?.email || "",
    }));
  }, [auth?.email, auth?.first_name, auth?.last_name]);

  const steps = useMemo(
    () => [
      "how_feeling_today",
      "name",
      "email",
      "let_us_know",
    ],
    []
  );

  const stepHasError = (stepIndex) =>
    STEP_FIELDS[stepIndex].some((field) => Boolean(errors[field]));

  const isStepComplete = (stepIndex) =>
    Object.keys(validateStep(stepIndex, data, t)).length === 0;

  const updateField = (field, value) => {
    setData((prev) => ({
      ...prev,
      [field]: value,
    }));

    setErrors((prev) => {
      if (!prev[field]) return prev;

      const nextErrors = { ...prev };
      delete nextErrors[field];
      return nextErrors;
    });
  };

  const handleEmotionSelect = (emotion) => {
    updateField("feeling", emotion);
  };

  const handleNext = () => {
    const nextErrors = validateStep(currentStep, data, t);

    if (Object.keys(nextErrors).length > 0) {
      setErrors((prev) => ({
        ...prev,
        ...nextErrors,
      }));
      return;
    }

    setCurrentStep((prev) => Math.min(prev + 1, steps.length - 1));
  };

  const handleBack = () => {
    setCurrentStep((prev) => Math.max(prev - 1, 0));
  };

  const handleStepSelect = (stepIndex) => {
    if (stepIndex <= currentStep) {
      setCurrentStep(stepIndex);
    }
  };

  const handleSubmit = () => {
    const nextErrors = validateAllSteps(data, t);

    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      setCurrentStep(getFirstInvalidStep(nextErrors));
      return;
    }

    const payload = {
      feeling: data.feeling,
      first_name: data.first_name.trim(),
      last_name: data.last_name.trim(),
      email: data.email.trim(),
      comment: data.comment.trim(),
    };

    setLoading(true);

    axios
      .post(
        `/organization/${program.organization_id}/program/${program.id}/feeling-survey`,
        payload
      )
      .then((res) => {
        if (res.status === 200) {
          flashSuccess(dispatch, t("submission_received"));
          setSubmitted(true);
        }
      })
      .catch((err) => {
        flashError(dispatch, err.response?.data || err.message);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  if (!program?.id || !program?.organization_id || !organization?.id) {
    return <div className="mp-loading">{t("loading")}</div>;
  }

  const renderCurrentStep = () => {
    const stepProps = {
      data,
      errors,
      onBack: handleBack,
      onNext: handleNext,
      onSubmit: handleSubmit,
      onFieldChange: updateField,
      loading,
    };

    if (currentStep === 0) {
      return (
        <FirstStep
          {...stepProps}
          onSelectEmotion={handleEmotionSelect}
        />
      );
    }

    if (currentStep === 1) {
      return <SecondStep {...stepProps} />;
    }

    if (currentStep === 2) {
      return <ThirdStep {...stepProps} />;
    }

    return <FourthStep {...stepProps} />;
  };

  const content = submitted ? (
    <div className="feeling-wizard__success">
      <div className="feeling-wizard__success-badge">{t("thank_you")}</div>
      <h2 className="feeling-wizard__success-title">{t("submission_received")}</h2>
      <p className="feeling-wizard__success-desc">{t("feeling_success_desc")}</p>
      <Link className="btn feeling-wizard__btn feeling-wizard__btn--primary" to="/participant/home">
        {t("nav_dashboard")}
      </Link>
    </div>
  ) : (
    <div className="feeling-wizard">
      <div className="feeling-wizard__progress">
        <div className="feeling-wizard__progress-copy">
          {t("feeling_step_counter", {
            current: currentStep + 1,
            total: steps.length,
          })}
        </div>
        <div className="feeling-wizard__stepper">
          {steps.map((stepLabel, index) => {
            let state = "upcoming";

            if (index === currentStep) {
              state = "current";
            } else if (stepHasError(index)) {
              state = "error";
            } else if (index < currentStep && isStepComplete(index)) {
              state = "complete";
            }

            return (
              <button
                key={stepLabel}
                type="button"
                className={`feeling-wizard__step is-${state}`}
                onClick={() => handleStepSelect(index)}
                disabled={index > currentStep}
              >
                <span className="feeling-wizard__step-index">
                  {state === "complete" ? "✓" : index + 1}
                </span>
                <span className="feeling-wizard__step-label">{t(stepLabel)}</span>
              </button>
            );
          })}
        </div>
      </div>

      {renderCurrentStep()}
    </div>
  );

  const FeelingClear = () => (
    <div className="feeling-page">
      <div className="feeling-page__header">
        <Container>
          <div className="feeling-page__header-inner">
            <div>
              <h1 className="feeling-page__title">{t("how_are_you_feeling")}</h1>
              <p className="feeling-page__desc">{t("feeling_page_desc")}</p>
            </div>
          </div>
        </Container>
      </div>

      <Container className="feeling-page__body">
        <ClearPageLayout>
          {content}
        </ClearPageLayout>
      </Container>
    </div>
  );

  const FeelingClassic = () => (
    <>
      <div className="mainboard">
        <img src={CLASSIC_IMG_BACK} alt={t("how_are_you_feeling")} />
        <div className="title">{t("how_are_you_feeling")}</div>
      </div>

      <Container>
        <ParticipantTabNavs />
      </Container>

      <Container>
        <Row>
          <Col md={9}>
            <div className="dashboard">
              <div className="feeling-page__classic-intro">
                <h3>{t("how_are_you_feeling")}</h3>
                <p>{t("feeling_page_desc")}</p>
              </div>
              {content}
            </div>
          </Col>
          <Col md={3}>
            <Sidebar />
          </Col>
        </Row>
      </Container>
    </>
  );

  return (
    (template?.name === "clear" && <FeelingClear />) ||
    (template?.name === "classic" && <FeelingClassic />) ||
    <FeelingClear />
  );
};

const mapStateToProps = (state) => ({
  auth: state.auth,
  program: state.program,
  organization: state.organization,
  template: state.template,
});

export default connect(mapStateToProps)(Feeling);
